import {
  AbstractControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { EMPTY, Subject, Subscription, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { AdvanceSearchService } from '../services/Advance-search/advance-search.service';

export type PincodeLookupStatus = 'idle' | 'incomplete' | 'loading' | 'valid' | 'error';

/**
 * Coordinates the shared pincode -> state/district form behavior used by
 * address, branch and property editors. The host form must contain pincode,
 * state and district controls.
 */
export class PincodeLookupController {
  isLoading = false;
  status: PincodeLookupStatus = 'idle';
  message = '';
  stateList: string[] = [];
  districtList: string[] = [];

  private verifiedPincode: string | null = null;
  private started = false;
  private districtRequestId = 0;
  private districtRequest?: Subscription;
  private readonly destroy$ = new Subject<void>();
  private readonly initialValidators: Record<'state' | 'district', ValidatorFn | null>;
  private readonly initiallyDisabled: Record<'state' | 'district', boolean>;

  constructor(
    private readonly form: FormGroup,
    private readonly advanceSearchService: AdvanceSearchService
  ) {
    this.initialValidators = {
      state: this.form.get('state')?.validator ?? null,
      district: this.form.get('district')?.validator ?? null,
    };
    this.initiallyDisabled = {
      state: this.form.get('state')?.disabled ?? false,
      district: this.form.get('district')?.disabled ?? false,
    };
  }

  get pincodeControl(): AbstractControl | null {
    return this.form.get('pincode');
  }

  start(): void {
    if (this.started || !this.pincodeControl) return;
    this.started = true;

    const pincodeControl = this.pincodeControl;
    pincodeControl.valueChanges
      .pipe(
        map((value) => this.sanitize(value)),
        tap((pincode) => {
          if (String(pincodeControl.value ?? '') !== pincode) {
            pincodeControl.setValue(pincode, { emitEvent: false });
          }
        }),
        distinctUntilChanged(),
        tap((pincode) => {
          this.clearLookupError();
          this.resetDependentFields();
          this.message = '';
          this.status = pincode
            ? (/^\d{6}$/.test(pincode) ? 'loading' : 'incomplete')
            : 'idle';
          this.isLoading = /^\d{6}$/.test(pincode);
        }),
        switchMap((pincode) => {
          if (!/^\d{6}$/.test(pincode)) return EMPTY;

          return this.advanceSearchService.fetchPincode(pincode).pipe(
            map((response) => ({ pincode, response, error: null })),
            catchError((error) => of({ pincode, response: null, error }))
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(({ pincode, response, error }) => {
        if (String(pincodeControl.value ?? '') !== pincode) return;

        this.isLoading = false;
        if (error) {
          this.setError(
            error?.error?.message || 'Could not verify this pincode. Please check it.'
          );
          return;
        }

        this.applyDetails(pincode, response?.data);
      });

    this.syncFromForm();
  }

  /**
   * Adopt a location already loaded from a trusted record without issuing a
   * second lookup. If the record only contains a pincode, verify it normally.
   */
  syncFromForm(): void {
    if (!this.started || !this.pincodeControl) return;

    const pincode = this.sanitize(this.pincodeControl.value);
    const state = String(this.form.get('state')?.value ?? '').trim();
    const district = String(this.form.get('district')?.value ?? '').trim();

    if (String(this.pincodeControl.value ?? '') !== pincode) {
      this.pincodeControl.setValue(pincode, { emitEvent: false });
    }

    this.cancelDistrictRequest();
    this.clearLookupError();
    this.isLoading = false;
    this.verifiedPincode = null;
    this.stateList = state ? [state] : [];
    this.districtList = district ? [district] : [];

    if (!pincode) {
      this.status = 'idle';
      this.message = '';
      return;
    }

    if (!/^\d{6}$/.test(pincode)) {
      this.status = 'incomplete';
      this.message = '';
      return;
    }

    if (state && district) {
      this.setLocationValidators('state');
      this.setLocationValidators('district');
      this.form.get('state')?.updateValueAndValidity({ emitEvent: false });
      this.form.get('district')?.updateValueAndValidity({ emitEvent: false });
      this.verifiedPincode = pincode;
      this.status = 'valid';
      this.message = 'Using the saved state and district for this pincode.';
      return;
    }

    // FormControl emits even when the value is unchanged, which routes this
    // value through the cancellable lookup stream configured in start().
    this.pincodeControl.setValue(pincode);
  }

  resolveSelectedDistrict(): void {
    const district = String(this.form.get('district')?.value ?? '');
    const pincode = String(this.pincodeControl?.value ?? '');
    if (!district || !/^\d{6}$/.test(pincode)) return;

    // A single returned state is already resolved; only a pincode spanning
    // multiple states needs the district-qualified request.
    if (this.stateList.length <= 1) {
      this.verifiedPincode = pincode;
      this.status = 'valid';
      this.message = 'State and district filled from the pincode.';
      return;
    }

    this.cancelDistrictRequest();
    const requestId = ++this.districtRequestId;
    this.clearLookupError();
    this.verifiedPincode = null;
    this.isLoading = true;
    this.status = 'loading';
    this.message = 'Resolving the state for this district.';
    this.form.get('state')?.setValue('', { emitEvent: false });
    this.form.get('state')?.updateValueAndValidity({ emitEvent: false });
    this.districtRequest = this.advanceSearchService
      .fetchPincode(pincode, district)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (!this.isCurrentDistrictRequest(requestId, pincode, district)) return;

          this.isLoading = false;
          const states = this.toStringList(response?.data?.state);
          if (states.length === 0) {
            this.setDistrictError('A state could not be resolved for this district.');
            return;
          }

          this.form.get('state')?.setValue(states[0], { emitEvent: false });
          this.form.get('state')?.updateValueAndValidity({ emitEvent: false });
          this.verifiedPincode = pincode;
          this.status = 'valid';
          this.message = 'State and district filled from the pincode.';
        },
        error: (error) => {
          if (!this.isCurrentDistrictRequest(requestId, pincode, district)) return;

          this.isLoading = false;
          this.setDistrictError(
            error?.error?.message || 'Could not verify the selected district.'
          );
        },
      });
  }

  isVerified(pincode: unknown): boolean {
    const value = String(pincode ?? '');
    if (value === '') return true;

    return this.verifiedPincode === value
      && Boolean(String(this.form.get('state')?.value ?? '').trim())
      && Boolean(String(this.form.get('district')?.value ?? '').trim());
  }

  destroy(): void {
    this.cancelDistrictRequest();
    this.isLoading = false;
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyDetails(pincode: string, data: any): void {
    this.stateList = this.toStringList(data?.state);
    this.districtList = this.toStringList(data?.district);

    if (this.stateList.length === 0 || this.districtList.length === 0) {
      this.setError('No location was found for this pincode.');
      return;
    }

    this.clearLookupError();
    const stateControl = this.form.get('state');
    const districtControl = this.form.get('district');
    this.setLocationValidators('state');
    this.setLocationValidators('district');

    if (this.districtList.length > 1) {
      districtControl?.enable({ emitEvent: false });
    } else {
      this.restoreDisabledState('district');
    }

    stateControl?.setValue(this.stateList.length === 1 ? this.stateList[0] : '', {
      emitEvent: false,
    });
    districtControl?.setValue(
      this.districtList.length === 1 ? this.districtList[0] : '',
      { emitEvent: false }
    );
    stateControl?.updateValueAndValidity({ emitEvent: false });
    districtControl?.updateValueAndValidity({ emitEvent: false });

    this.verifiedPincode = pincode;
    this.status = 'valid';
    this.message = this.districtList.length > 1
      ? 'Select the district for this pincode.'
      : 'State and district filled from the pincode.';

    if (this.stateList.length > 1 && this.districtList.length === 1) {
      this.resolveSelectedDistrict();
    }
  }

  private resetDependentFields(): void {
    this.cancelDistrictRequest();
    this.verifiedPincode = null;
    this.stateList = [];
    this.districtList = [];

    for (const controlName of ['state', 'district'] as const) {
      const control = this.form.get(controlName);
      control?.setValidators(this.initialValidators[controlName]);
      control?.setValue('', { emitEvent: false });
      this.restoreDisabledState(controlName);
      control?.updateValueAndValidity({ emitEvent: false });
    }
  }

  private setError(message: string): void {
    const control = this.pincodeControl;
    control?.setErrors({ ...(control.errors || {}), pincodeLookup: true });
    control?.markAsTouched();
    this.verifiedPincode = null;
    this.status = 'error';
    this.message = message;
    this.stateList = [];
    this.districtList = [];
    this.form.patchValue({ state: '', district: '' }, { emitEvent: false });
  }

  private setDistrictError(message: string): void {
    const control = this.pincodeControl;
    control?.setErrors({ ...(control.errors || {}), pincodeLookup: true });
    control?.markAsTouched();
    this.verifiedPincode = null;
    this.status = 'error';
    this.message = message;
    this.form.get('state')?.setValue('', { emitEvent: false });
    this.form.get('state')?.updateValueAndValidity({ emitEvent: false });
  }

  private clearLookupError(): void {
    const control = this.pincodeControl;
    const errors = control?.errors;
    if (!control || !errors?.['pincodeLookup']) return;

    const remainingErrors = { ...errors };
    delete remainingErrors['pincodeLookup'];
    control.setErrors(
      Object.keys(remainingErrors).length > 0 ? remainingErrors : null,
      { emitEvent: false }
    );
  }

  private setLocationValidators(controlName: 'state' | 'district'): void {
    const control = this.form.get(controlName);
    control?.setValidators(this.initialValidators[controlName]);
    control?.addValidators(Validators.required);
  }

  private restoreDisabledState(controlName: 'state' | 'district'): void {
    const control = this.form.get(controlName);
    if (this.initiallyDisabled[controlName]) {
      control?.disable({ emitEvent: false });
    } else {
      control?.enable({ emitEvent: false });
    }
  }

  private cancelDistrictRequest(): void {
    this.districtRequestId += 1;
    this.districtRequest?.unsubscribe();
    this.districtRequest = undefined;
  }

  private isCurrentDistrictRequest(
    requestId: number,
    pincode: string,
    district: string
  ): boolean {
    return requestId === this.districtRequestId
      && String(this.pincodeControl?.value ?? '') === pincode
      && String(this.form.get('district')?.value ?? '') === district;
  }

  private sanitize(value: unknown): string {
    return String(value ?? '').replace(/\D/g, '').slice(0, 6);
  }

  private toStringList(value: unknown): string[] {
    return Array.isArray(value)
      ? value.map((item) => String(item).trim()).filter(Boolean)
      : [];
  }
}
